<template>
  <div
    v-show="shouldShow"
    v-tooltip.html.placementRight="tooltipMethod"
    class="sf-drawer-toggle__container"
    :data-testid="testId"
    @click.stop.prevent="toggleDrawer"
  >
    <slot />
    <i v-if="hasNewItems" class="sf-drawer-toggle__new-indicator"></i>
  </div>
</template>

<script lang="ts" setup>
import { LeftDrawerContent, RightDrawerContent } from '@app/shared/types/drawer'

import { storeToRefs } from 'pinia'
import { computed, onMounted, watch } from 'vue'

import { isUsingNewLayout } from '@app/adapters/silverfin'
import { useDrawer } from '@app/shared/pinia/drawer'
import {
  readPersistedLeftCollapsed,
  readPersistedRightContent,
} from '@app/shared/pinia/drawer-hydration'
import getPiniaInstance from '@app/shared/pinia/instance'

import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'

import {
  useTalkingPointsSidebarOptions,
  type SetTalkingPointsSidebarOptionsPayload,
} from '@app/application/client_meetings/store/use-talking-points-sidebar-options'

import { SfLog } from '@app/adapters/logger'

interface BaseProps {
  hideIfOpen?: boolean
  collapseTooltip?: string
  expandTooltip?: string
}

interface LeftDrawerProps extends BaseProps {
  position: 'left'
  content: LeftDrawerContent
}

interface RightDrawerProps extends BaseProps {
  position: 'right'
  content: RightDrawerContent
  options?: SetTalkingPointsSidebarOptionsPayload
}

type Props = LeftDrawerProps | RightDrawerProps

const props = defineProps<Props>()

const emits = defineEmits(['shown', 'hidden'])

const hideIfOpen = computed(() => props.hideIfOpen ?? false)

const auditsStore = useAuditsStore(getPiniaInstance())
const drawerStore = useDrawer(getPiniaInstance())
const { open, leftContent, rightContent } = storeToRefs(drawerStore)
const { changesSinceLastVisit } = storeToRefs(auditsStore)

const talkingPointsSidebarOptionsStore = useTalkingPointsSidebarOptions(
  getPiniaInstance()
)

const isContextOpen = computed(() => {
  if (isUsingNewLayout()) {
    return props.position === 'left'
      ? leftContent.value === props.content
      : rightContent.value === props.content
  }

  return (
    open.value.content === props.content &&
    open.value.direction === props.position
  )
})

// Intentionally a function (not a computed) — the v-tooltip directive calls it
// each time the tooltip opens, reading the current reactive isContextOpen value.
// The directive's `updated` hook disposes and recreates the tooltip on re-render,
// ensuring the returned text stays in sync with the drawer state.
const tooltipMethod = () => {
  if (!props.collapseTooltip && !props.expandTooltip) return ''

  return isContextOpen.value ? props.collapseTooltip : props.expandTooltip
}

const shouldShow = computed(() => {
  return hideIfOpen.value ? !isContextOpen.value : true
})

const hasNewItems = computed(() => {
  switch (props.content) {
    case RightDrawerContent.AuditLog:
      return changesSinceLastVisit.value.count > 0
    default:
      return false
  }
})

const testId = computed(() => {
  return `sf-drawer-toggle-${props.position}-${props.content}-${
    isContextOpen.value ? 'open' : 'closed'
  }`
})

function toggleDrawer() {
  if (props.position === 'left') {
    drawerStore.toggleDrawer({
      content: props.content,
      direction: 'left',
    })
  } else {
    switch (props.content) {
      case RightDrawerContent.TalkingPoints:
        if (props.options) {
          talkingPointsSidebarOptionsStore.setOptions(props.options)
        } else {
          SfLog.warn(
            'Opening talking-points drawer without options; sidebar may render with stale context.'
          )
        }
        break
      default:
        break
    }

    drawerStore.toggleDrawer({
      content: props.content,
      direction: 'right',
    })
  }
}

onMounted(() => {
  // Pages that own the right drawer themselves (e.g. the insights cell-details
  // drawer) mark it suppressed so hydration doesn't restore the global
  // comms/audit drawer over their transient one, leaving it open-but-empty.
  const suppressRightDrawer =
    document.getElementById('right-drawer')?.dataset.suppressRightDrawer ===
    'true'

  if (isUsingNewLayout()) {
    const persisted = readPersistedRightContent()
    const restorable =
      suppressRightDrawer && persisted !== RightDrawerContent.AiChat
        ? null
        : persisted

    drawerStore.hydrateFromPreferences({
      collapsed: readPersistedLeftCollapsed(),
      rightContent: restorable,
    })
    return
  }

  const direction =
    window?.Silverfin?.currentUserPreferences?.open_sidepane?.position
  const content =
    window?.Silverfin?.currentUserPreferences?.open_sidepane?.content

  switch (direction) {
    case 'left':
      if (content === 'default') {
        drawerStore.openDrawerDefault({
          direction,
          content: LeftDrawerContent.Default,
        })
      }
      break
    case 'right':
      if (suppressRightDrawer) break
      switch (content) {
        case 'audit-log':
          drawerStore.openDrawerDefault({
            direction,
            content: RightDrawerContent.AuditLog,
          })
          break
        case 'communication':
          drawerStore.openDrawerDefault({
            direction,
            content: RightDrawerContent.Communication,
          })
          break
        default:
          break
      }
      break
    default:
      break
  }
})

watch(
  shouldShow,
  (newValue, oldValue) => {
    if (!newValue && newValue !== oldValue) {
      emits('hidden')
    }

    if (newValue && newValue !== oldValue) {
      emits('shown')
    }
  },
  {
    immediate: true,
  }
)
</script>
<style lang="scss" scoped>
@import './sf-drawer-toggle';
</style>
